/**
 * IndexedDB 存储工具
 * 用于替代 localStorage，支持更大的存储容量
 */

import type { Ref } from 'vue'

import localforage from 'localforage'

import { ref, watch } from 'vue'
import { toast } from 'vue-sonner'

// 创建专用的 localforage 实例
const storageInstance = localforage.createInstance({
  name: 'meowchat',
  storeName: 'app_data',
  description: 'MeowChat application data storage',
})

/**
 * 创建一个使用 IndexedDB 存储的响应式引用
 * 类似于 useLocalStorage，但使用 IndexedDB 作为后端存储
 *
 * @param key 存储键名
 * @param defaultValue 默认值
 * @param options 选项
 * @param options.serializer 自定义序列化器
 * @param options.serializer.read 从字符串读取数据
 * @param options.serializer.write 将数据写成字符串
 * @param options.onError 存储操作失败时的回调
 * @param options.debounceMs 写入防抖时间（毫秒）
 * @param options.deepWatch 是否深度监听数据变化
 */
export function useIndexedDBStorage<T>(
  key: string,
  defaultValue: T,
  options: {
    serializer?: {
      read: (v: string) => T
      write: (v: T) => string
    }
    onError?: (error: Error) => void
    debounceMs?: number
    /** 是否使用深度监听。默认 true。设为 false 时仅监听引用变化，需手动调用 notifyChange() 触发保存 */
    deepWatch?: boolean
  } = {},
): {
  data: Ref<T>
  isLoaded: Ref<boolean>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  loadError: Ref<Error | null>
  reload: () => Promise<void>
  save: () => Promise<void>
  /** 手动通知数据已变更，触发防抖保存（deepWatch=false 时用于数组 push/splice 等原地变更） */
  notifyChange: () => void
} {
  const data = ref<T>(defaultValue) as Ref<T>
  const isLoaded = ref(false)
  const isLoading = ref(true)
  const error = ref<Error | null>(null)
  const loadError = ref<Error | null>(null)

  // 防抖定时器
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  const debounceMs = options.debounceMs ?? 300

  function canPersist(): boolean {
    return isLoaded.value && !isLoading.value && !loadError.value
  }

  function deserialize(value: string): T {
    if (options.serializer?.read)
      return options.serializer.read(value)
    try {
      return JSON.parse(value) as T
    }
    catch {
      return value as unknown as T
    }
  }

  // 从 IndexedDB 加载数据
  async function load(): Promise<void> {
    isLoading.value = true
    loadError.value = null
    error.value = null

    try {
      const stored = await storageInstance.getItem<string>(key)

      if (stored !== null) {
        data.value = deserialize(stored)
      }
      else {
        // 尝试从 localStorage 迁移数据
        const localStorageData = localStorage.getItem(key)
        if (localStorageData) {
          console.info(`🔄 正在从 localStorage 迁移数据: ${key}`)
          data.value = deserialize(localStorageData)
          // 只有确认 IndexedDB 写入成功后才删除旧副本。
          if (await write()) {
            localStorage.removeItem(key)
            console.info(`✅ 数据迁移完成: ${key}`)
          }
          else {
            console.warn(`⚠️ IndexedDB 写入失败，保留 localStorage 副本: ${key}`)
          }
        }
      }

      isLoaded.value = true
    }
    catch (e) {
      const err = e as Error
      error.value = err
      loadError.value = err
      console.error(`❌ 加载 IndexedDB 数据失败 (${key})，已暂停该键的写入以保护原数据:`, err)
      options.onError?.(err)
      // 即使加载失败也标记为已加载，避免 waitForStorage 永远不 resolve 导致应用挂起
      isLoaded.value = true
    }
    finally {
      isLoading.value = false
    }
  }

  // 保存数据到 IndexedDB
  async function write(): Promise<boolean> {
    try {
      let valueToStore: string

      if (options.serializer?.write) {
        valueToStore = options.serializer.write(data.value)
      }
      else {
        valueToStore = JSON.stringify(data.value)
      }

      await storageInstance.setItem(key, valueToStore)
      error.value = null
      toast.dismiss(`storage-write-${key}`)
      return true
    }
    catch (e) {
      error.value = e as Error
      console.error(`❌ 保存 IndexedDB 数据失败 (${key}):`, e)
      toast.error(error.value.name === 'QuotaExceededError'
        ? '本地存储空间不足，最近更改尚未保存。请释放设备空间后重试。'
        : '本地保存失败，最近更改尚未保存。请保留此页面后重试。', {
        id: `storage-write-${key}`,
        duration: 10_000,
        action: { label: '重试保存', onClick: () => { void save() } },
      })
      options.onError?.(e as Error)
      return false
    }
  }

  async function save(): Promise<void> {
    if (!canPersist())
      return
    await write()
  }

  // 防抖保存
  function debouncedSave(): void {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
      void save()
      debounceTimer = null
    }, debounceMs)
  }

  // 手动变更通知（用于 deepWatch=false 模式）
  const _version = ref(0)
  function notifyChange(): void {
    _version.value++
  }

  const useDeepWatch = options.deepWatch !== false

  if (useDeepWatch) {
    // 深度监听模式：自动检测嵌套变更（对大型数组开销较大）
    watch(
      () => data.value,
      () => {
        if (canPersist()) {
          debouncedSave()
        }
      },
      { deep: true },
    )
  }
  else {
    // 浅监听模式：仅监听引用替换 + 手动 notifyChange()
    watch(
      () => data.value,
      () => {
        if (canPersist()) {
          debouncedSave()
        }
      },
    )
    watch(_version, () => {
      if (canPersist()) {
        debouncedSave()
      }
    })
  }

  // 立即加载数据
  load()

  return {
    data,
    isLoaded,
    isLoading,
    error,
    loadError,
    reload: load,
    save,
    notifyChange,
  }
}

/**
 * 等待 IndexedDB 存储加载完成
 */
export async function waitForStorage<T>(storage: ReturnType<typeof useIndexedDBStorage<T>>): Promise<void> {
  if (storage.isLoaded.value) {
    return
  }

  return new Promise((resolve) => {
    const unwatch = watch(
      () => storage.isLoaded.value,
      (loaded) => {
        if (loaded) {
          unwatch()
          resolve()
        }
      },
      { immediate: true },
    )
  })
}

/**
 * 清理所有 IndexedDB 存储数据
 */
export async function clearAllStorage(): Promise<void> {
  await storageInstance.clear()
  console.info('✅ 已清理所有 IndexedDB 存储数据')
}

/**
 * 获取存储使用情况
 */
export async function getStorageUsage(): Promise<{ keys: string[], estimatedSize: number }> {
  const keys: string[] = []
  let estimatedSize = 0

  await storageInstance.iterate((value, key) => {
    keys.push(key)
    if (typeof value === 'string') {
      estimatedSize += value.length * 2 // UTF-16 编码
    }
  })

  return { keys, estimatedSize }
}
